import numeral from 'numeral';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
// styles
import { screen, colors } from 'components/styles/variables';
import { trackDealerPagesClick } from 'lib/Tracking/DealerPages';
import { DealerPagesEventType } from 'lib/EventSchema';
import TrackingDataContext from 'contexts/TrackingDataContext';
import { useCallback, useContext } from 'react';

interface CarouselVehicleCardProps {
  position: number;
  year: number;
  make: string;
  model: string;
  mileageUnformatted: number | null;
  priceUnformatted: number;
  vdpUrl: string;
  photoUrls: string[];
}

export default function CarouselVehicleCard({
  position,
  year,
  make,
  model,
  mileageUnformatted: mileage,
  priceUnformatted,
  vdpUrl,
  photoUrls,
}: CarouselVehicleCardProps): JSX.Element {
  const { t } = useTranslation();
  const { dealerPageUrl } = useContext(TrackingDataContext);
  const price = numeral(priceUnformatted).format('$0,0');

  const onClick = useCallback((): void => {
    trackDealerPagesClick({
      type: DealerPagesEventType.listing_click,
      label: String(position),
      targetPageUrl: vdpUrl,
      dealerPageUrl,
    });
  }, [position, vdpUrl, dealerPageUrl]);

  return (
    <>
      <Link href={vdpUrl} legacyBehavior>
        <a className="vehicle" data-testid="vehicle" onClick={onClick}>
          <div className="photo" />
          <div className="info">
            <div className="details">
              <div className="primary">{t('comparison.ymm', { year, make, model })}</div>
              <div className="secondary">{t('comparison.listings.mileage', { mileage })}</div>
            </div>
            <div className="price">{numeral(price).format('$0,0')}</div>
          </div>
        </a>
      </Link>
      <style jsx>{`
        .vehicle {
          display: block;
          float: left;
          margin-left: 15px;
          border: 1px solid ${colors.G2};
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
          border-radius: 8px;
          width: 80vw;
          max-width: 300px;
          min-height: 263px;
          color: black;
          text-decoration: none;
        }

        @media (min-width: ${screen.smMin}) {
          .vehicle {
            margin-left: 0;
          }
        }

        .photo {
          width: 100%;
          height: 175px;
          border-top-left-radius: 7px;
          border-top-right-radius: 7px;
          background-repeat: none;
          background-size: cover;
          background-position: center center;
          background-image: url(${photoUrls[0]}),
            url(https://www.autolist.com/assets/listings/default_car.jpg);
        }

        .info {
          display: flex;
          padding: 15px 10px;
        }

        .details {
          width: 67%;
        }

        .primary,
        .price {
          font-weight: 900;
          font-size: 18px;
          line-height: 18px;
        }

        .price {
          width: 33%;
          text-align: right;
        }

        .secondary {
          margin-top: 5px;
          font-size: 14px;
          line-height: 14px;
        }
      `}</style>
    </>
  );
}
